import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Store} from '../../../features/stores/models/store.model';
import {Visit} from '../../../features/visits/models/visit.model';
import {MapPin, Phone, User, Building, Clock, DollarSign, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, MessageCircle, X, Eye, Calendar, ExternalLink, Package} from 'lucide-react';
import StoreImageGallery from './StoreImageGallery';
import {CreditInfoCard} from '../../../features/stores/components/cards/CreditInfoCard';
import {StoreDetailsBySections} from '../../../features/stores/components/info-sections/StoreDetailsBySections';
import {useStoreSelectionStore} from '../../../features/stores/store/StoreSelectionStore';
import {useStoreVisits} from '../../../features/visits/hooks/useStoreVisits';
import VisitListCard from './VisitListCard';
import {formatCurrency} from '../../utils/numberUtils';

// -----------------------------------------------------------------------------
// Compound: Visits block (reused in sections and legacy branches)
// -----------------------------------------------------------------------------
interface StoreDetailsVisitsBlockProps {
  visitHistory: Visit[];
  approvedVisits: Visit[] | undefined;
  loading: boolean;
  error: string | null;
}
function StoreDetailsVisitsBlock({
  visitHistory,
  approvedVisits,
  loading,
  error
}: StoreDetailsVisitsBlockProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        الزيارات المكتملة
      </h3>
      {approvedVisits && approvedVisits.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Clock className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <div className="font-semibold text-green-900">زيارات معتمدة</div>
              <div className="text-sm text-green-700">يوجد {approvedVisits.length} زيارة معتمدة</div>
            </div>
          </div>
        </div>
      )}
      <VisitListCard visits={visitHistory} loading={loading} error={error} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Compound: Link to stores management (reused in sections and legacy branches)
// -----------------------------------------------------------------------------
function StoreDetailsLinkToStoresCard({storeId}: {storeId: number}) {
  return (
    <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Package className="w-5 h-5 text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-brand-900 mb-1">سجل التسليمات وتفاصيل المتجر</h3>
          <p className="text-xs text-brand-800 mb-3">لعرض سجل التسليمات والتفاصيل الكاملة، انتقل إلى صفحة إدارة المتاجر.</p>
          <Link
            to="/dashboard/stores"
            state={{openStoreId: storeId}}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            عرض تفاصيل المتجر في إدارة المتاجر
          </Link>
        </div>
      </div>
    </div>
  );
}

interface StoreDetailsProps {
  store: Store | undefined;
  onClose?: () => void;
}

const StoreDetails: React.FC<StoreDetailsProps> = ({store, onClose}) => {
  const [showFinancialModal, setShowFinancialModal] = useState(false);

  // Use stores for data management
  const {selectedStoreDetails, isLoadingDetails, fetchStoreDetails} = useStoreSelectionStore();

  // Use visits for visit data
  const {visitHistory, approvedVisits, loading: visitsLoading, error: visitsError} = useStoreVisits(store?.id);

  // Fetch store details when store changes
  useEffect(() => {
    if (store?.id) {
      // Fetch full store details to get contact information
      fetchStoreDetails(store.id.toString());
    }
  }, [store?.id, fetchStoreDetails]);

  if (!store) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-medium mb-2">اختر متجراً من الخريطة</p>
          <p className="text-sm">انقر على أي متجر في الخريطة لعرض تفاصيله</p>
        </div>
      </div>
    );
  }

  const getPaymentStatusText = (status: string) => {
    switch (status) {
    case 'paid': return 'مدفوع';
    case 'overdue': return 'متأخر';
    default: return 'معلق';
    }
  };

  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
    case 'paid': return <CheckCircle className="w-4 h-4" />;
    case 'overdue': return <AlertTriangle className="w-4 h-4" />;
    default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
    case 'paid': return 'text-green-600 bg-green-50 border-green-200';
    case 'overdue': return 'text-red-600 bg-red-50 border-red-200';
    default: return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getQuickActions = () => {
    const phoneNumber = (selectedStoreDetails?.phoneNumber && selectedStoreDetails.phoneNumber !== 'غير محدد')
      ? selectedStoreDetails.phoneNumber
      : store.phoneNumber;

    return [
      {
        icon: <Phone className="w-4 h-4" />,
        label: 'اتصال',
        action: () => {
          if (phoneNumber && phoneNumber !== 'غير محدد') {
            window.open(`tel:${phoneNumber}`, '_self');
          }
        },
        color: 'bg-green-100 text-green-600 hover:bg-green-200',
        disabled: !phoneNumber || phoneNumber === 'غير محدد'
      },
      {
        icon: <MessageCircle className="w-4 h-4" />,
        label: 'رسالة',
        action: () => {
          if (phoneNumber && phoneNumber !== 'غير محدد') {
            window.open(`https://wa.me/${phoneNumber}`, '_blank');
          }
        },
        color: 'bg-brand-100 text-brand-600 hover:bg-brand-200',
        disabled: !phoneNumber || phoneNumber === 'غير محدد'
      },
      {
        icon: <MapPin className="w-4 h-4" />,
        label: 'موقع',
        action: () => window.open(`https://maps.google.com/?q=${store.latitude},${store.longitude}`, '_blank'),
        color: 'bg-purple-100 text-purple-600 hover:bg-purple-200',
        disabled: false
      }
    ];
  };

  // Single page content - no tabs needed

  return (
    <div className="bg-white rounded-xl shadow-lg h-full flex flex-col overflow-hidden">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {store.profileImageUrl ? (
              <img
                src={store.profileImageUrl}
                alt={store.name}
                className="w-20 h-20 rounded-xl object-cover border-4 border-white shadow-md"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center border-4 border-white shadow-md">
                <Building className="w-10 h-10 text-white" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {store.name}
              </h2>
              <p className="text-gray-600 flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4" />
                {store.address}
              </p>

              {/* Store ID and Manager */}
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  <span>ID: {store.id}</span>
                </div>
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>
                    {isLoadingDetails ? 'جاري التحميل...' :
                     (selectedStoreDetails?.managerName && selectedStoreDetails.managerName !== 'غير محدد')
                       ? selectedStoreDetails.managerName
                       : store.managerName}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
              aria-label="إغلاق"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Single Page Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-brand-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">إجراءات سريعة</h3>
          <div className="flex gap-2">
            {getQuickActions().map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                disabled={action.disabled || isLoadingDetails}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                  action.disabled || isLoadingDetails
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : action.color
                }`}
              >
                {action.icon}
                <span className="text-sm font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {selectedStoreDetails?.sections && selectedStoreDetails.sections.length > 0 ? (
          <>
            <StoreDetailsBySections store={selectedStoreDetails} />
            <StoreDetailsVisitsBlock
              visitHistory={visitHistory}
              approvedVisits={approvedVisits}
              loading={visitsLoading}
              error={visitsError}
            />
            <StoreDetailsLinkToStoresCard storeId={store.id} />
          </>
        ) : (
          <>
            {/* Status Overview - حالة الدفع فقط */}
            <div className="grid grid-cols-1 gap-4">
              <div className={`p-4 rounded-xl border ${getPaymentStatusColor(store.paymentStatus)}`}>
                <div className="flex items-center gap-3">
                  {getPaymentStatusIcon(store.paymentStatus)}
                  <div>
                    <div className="text-xs font-medium opacity-75">حالة الدفع</div>
                    <div className="font-bold text-sm">{getPaymentStatusText(store.paymentStatus)}</div>
                  </div>
                </div>
              </div>
            </div>

            <StoreDetailsVisitsBlock
              visitHistory={visitHistory}
              approvedVisits={approvedVisits}
              loading={visitsLoading}
              error={visitsError}
            />

            {/* Contact Information */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                معلومات الاتصال
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">مدير المتجر</div>
                    <div className="font-semibold text-gray-900">
                      {isLoadingDetails ? 'جاري التحميل...' : (selectedStoreDetails?.managerName && selectedStoreDetails.managerName !== 'غير محدد') ? selectedStoreDetails.managerName : store.managerName}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">رقم الهاتف</div>
                    <div className="font-semibold text-gray-900 text-left" dir="ltr">
                      {isLoadingDetails ? 'جاري التحميل...' : (selectedStoreDetails?.phoneNumber && selectedStoreDetails.phoneNumber !== 'غير محدد') ? selectedStoreDetails.phoneNumber : store.phoneNumber}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* المعلومات المالية */}
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    المعلومات المالية
                  </h3>
                  <button
                    onClick={() => setShowFinancialModal(true)}
                    className="p-2 bg-brand-100 text-brand-600 rounded-lg hover:bg-brand-200 transition-colors"
                    title="عرض التفاصيل الكاملة"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">إجمالي المبيعات</span>
                    </div>
                    <div className="font-semibold text-green-900 text-lg">
                      {store.creditInfo?.totalSales ? formatCurrency(store.creditInfo.totalSales) : 'غير متوفر'}
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-700">المبلغ المستحق</span>
                    </div>
                    <div className="font-semibold text-red-900 text-lg">
                      {store.creditInfo?.totalDue ? formatCurrency(store.creditInfo.totalDue) : 'غير متوفر'}
                    </div>
                  </div>
                </div>
              </div>
              <StoreDetailsLinkToStoresCard storeId={store.id} />
            </div>

            <div className="space-y-3">
              <StoreImageGallery images={store.images || []} profileImageUrl={store.profileImageUrl} storeName={store.name} />
            </div>
          </>
        )}
      </div>

      {/* Modals */}

      {/* Financial Details Modal - You can create this if needed */}
      {showFinancialModal && store.creditInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">التفاصيل المالية</h2>
                <button
                  onClick={() => setShowFinancialModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <CreditInfoCard creditInfo={store.creditInfo} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreDetails;
